
import {polygonArea} from "./measurementGeometry.js";

// Pure, DOM/THREE-free 2D polygon validity check - used as a post-condition
// after any whole-Polygon editing operation (Forenkle/Ortogonaliser/Snap
// alle punkter - see VectorEditingMenu.js) so an operation can never leave
// behind a self-intersecting, degenerate, or too-small ring. No such check
// existed anywhere in this codebase before (confirmed: polylineOffset.js's
// own intersectLines2D() is an INFINITE-line intersection used only for
// miter joins, not a bounded segment-segment test) - this is the standard,
// textbook orientation-based bounded-segment-intersection test, not a
// general GIS framework.
//
// Only XY is considered (same 2D-footprint convention polygonArea()/
// FeatureVisual._rebuildFill() already use consistently for Polygon) -
// a ring's validity as a closed AREA is a planimetric concept here, not a
// 3D one.

const EPS = 1e-6; // orientation zero-threshold, in metres^2 (cross-product units) - see this file's own reasoning in isValidPolygonRing()'s doc comment

function orientation(ax, ay, bx, by, cx, cy){
	const val = (by - ay) * (cx - bx) - (bx - ax) * (cy - by);
	if(Math.abs(val) < EPS) return 0; // collinear
	return val > 0 ? 1 : 2;
}

function onSegment(ax, ay, bx, by, px, py){
	return px <= Math.max(ax, bx) + EPS && px >= Math.min(ax, bx) - EPS
		&& py <= Math.max(ay, by) + EPS && py >= Math.min(ay, by) - EPS;
}

// Standard bounded segment-segment intersection test (orientation/
// straddle test), including the collinear-touching/overlapping special
// cases - two segments that merely TOUCH (not just cross) also count as
// intersecting here, since a polygon ring where two non-adjacent edges
// touch is just as invalid (a pinched/bowtie ring) as one where they cross.
function segmentsIntersect(a1, a2, b1, b2){
	const [ax1, ay1] = a1, [ax2, ay2] = a2, [bx1, by1] = b1, [bx2, by2] = b2;

	const o1 = orientation(ax1, ay1, ax2, ay2, bx1, by1);
	const o2 = orientation(ax1, ay1, ax2, ay2, bx2, by2);
	const o3 = orientation(bx1, by1, bx2, by2, ax1, ay1);
	const o4 = orientation(bx1, by1, bx2, by2, ax2, ay2);

	if(o1 !== o2 && o3 !== o4) return true;

	if(o1 === 0 && onSegment(ax1, ay1, ax2, ay2, bx1, by1)) return true;
	if(o2 === 0 && onSegment(ax1, ay1, ax2, ay2, bx2, by2)) return true;
	if(o3 === 0 && onSegment(bx1, by1, bx2, by2, ax1, ay1)) return true;
	if(o4 === 0 && onSegment(bx1, by1, bx2, by2, ax2, ay2)) return true;

	return false;
}

// points: array of [x,y] or [x,y,z] (or {x,y[,z]}) - same duck-typed
//2-or-3-element/positional convention polygonArea() already accepts.
// Returns true iff the ring (points[0..n-1], implicitly closed back to
// points[0]) is a SIMPLE polygon: at least 3 vertices, non-degenerate area
// (> minArea, guards against a collapsed/zero-width ring), and no two
// non-adjacent edges intersect or touch.
export function isValidPolygonRing(points, minArea = 1e-6){
	const n = points.length;
	if(n < 3) return false;

	const flat = points.map((p) => (Array.isArray(p) ? [p[0], p[1]] : [p.x, p.y]));
	if(polygonArea(flat) <= minArea) return false;

	for(let i = 0; i < n; i++){
		const a1 = flat[i], a2 = flat[(i + 1) % n];
		for(let j = i + 1; j < n; j++){
			// two edges that share a vertex (adjacent in the ring, including
			// the wrap-around pair edge[n-1]/edge[0]) are expected to touch
			// exactly there - only non-adjacent pairs are a validity concern
			const adjacent = (j === (i + 1) % n) || ((j + 1) % n === i);
			if(adjacent) continue;

			const b1 = flat[j], b2 = flat[(j + 1) % n];
			if(segmentsIntersect(a1, a2, b1, b2)) return false;
		}
	}

	return true;
}
